import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Mail, Edit, Copy, Trash2, RefreshCw, Search } from 'lucide-react';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { EmailTemplateModal } from './EmailTemplateModal';
import { EmailTemplate } from '@/types/emailTemplate';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { Input } from '@/components/ui/input';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';

export const EmailTemplatesTab = () => {
  const { t } = useTranslation();
  const { 
    templates, 
    loading, 
    fetchTemplates, 
    deleteTemplate, 
    duplicateTemplate 
  } = useEmailTemplates();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleDuplicate = async (template: EmailTemplate) => {
    try {
      await duplicateTemplate(template.id);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      try {
        await deleteTemplate(deleteConfirmId);
        setDeleteConfirmId(null);
      } catch (error) {
        // Error handling is done in the hook
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {t('admin.emailTemplates.title', 'Email Templates')}
              </CardTitle>
              <CardDescription>
                {t('admin.emailTemplates.description', 'Manage email templates for automated communications')}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={fetchTemplates} 
                variant="outline" 
                size="sm"
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                {t('admin.refresh', 'Refresh')}
              </Button>
              <Button onClick={handleCreateNew} className="bg-digger-blue hover:bg-digger-blue-light">
                <Plus className="mr-2 h-4 w-4" />
                {t('admin.emailTemplates.createNew', 'Create Template')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={t('admin.emailTemplates.search', 'Search templates...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-digger-blue"></div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                {searchTerm 
                  ? t('admin.emailTemplates.noResults', 'No templates found')
                  : t('admin.emailTemplates.noTemplates', 'No email templates yet')
                }
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchTerm
                  ? t('admin.emailTemplates.tryDifferentSearch', 'Try adjusting your search terms')
                  : t('admin.emailTemplates.createFirst', 'Create your first email template to get started')
                }
              </p>
              {!searchTerm && (
                <Button onClick={handleCreateNew} className="bg-digger-blue hover:bg-digger-blue-light">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('admin.emailTemplates.createNew', 'Create Template')}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredTemplates.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold truncate">{template.name}</h3>
                          <Badge variant="secondary" className="text-xs">
                            {t('admin.emailTemplates.template', 'Template')}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          <strong>{t('admin.emailTemplates.subject', 'Subject')}:</strong> {template.subject}
                        </p>
                        {template.description && (
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {template.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>
                            {t('admin.emailTemplates.created', 'Created')} {formatDistanceToNow(new Date(template.created_at))} {t('admin.emailTemplates.ago', 'ago')}
                          </span>
                          <span>
                            {t('admin.emailTemplates.updated', 'Updated')} {formatDistanceToNow(new Date(template.updated_at))} {t('admin.emailTemplates.ago', 'ago')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(template)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {t('admin.emailTemplates.edit', 'Edit')}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                              <Copy className="h-4 w-4 mr-2" />
                              {t('admin.emailTemplates.duplicate', 'Duplicate')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteConfirmId(template.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t('admin.emailTemplates.delete', 'Delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <EmailTemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        template={editingTemplate}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.emailTemplates.deleteConfirmTitle', 'Delete Email Template')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.emailTemplates.deleteConfirmDescription', 'This action cannot be undone. This will permanently delete the email template.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.emailTemplates.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('admin.emailTemplates.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
